import { Table } from 'antd';
import { rightsService } from "../../../../services/rightsService";
import { forwardRef, useEffect, useState } from 'react';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { frenchMonth } from '../../../../variables/functions';
import { exportBordereauExcel, moisPeriodeLabel } from './bordereauExcel';
import { getCompagnieLogo } from '../../../../hooks/useFilesHooks';
import BordereauPrintTable from './BordereauPrintTable';

const BordereauAnnulations = forwardRef(({
    data,
    columns,
    selectedRows,
    onSelectionChange,
    onValidate,
    onPrint,
    isValidated,
    isDisabled,
    loading,
    categorie,
    periode,
    compagnie
}, ref) => {
    const [isPrinting, setIsPrinting] = useState(false);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // Logo de la compagnie pour l'en-tête répété (BordereauPrintTable).
    useEffect(() => {
        if (compagnie?.logo && compagnie?.code) {
            getCompagnieLogo(compagnie.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    const blob = new Blob([imageBytes], { type: 'image/png' });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((e) => console.log(e));
        }
    }, [compagnie]);

    // Select all rows by default when data changes and no selection exists
    useEffect(() => {
        if (data.length > 0 && selectedRows.length === 0 && !isValidated) { // défaut = tout sélectionné, même désactivé (sinon TOTAL=0)
            onSelectionChange(data); // all rows selected initially
        }
    }, [data, selectedRows, onSelectionChange, isValidated, isDisabled]);

    // Décocher des lignes pendant une cession est SOUS HABILITATION (HLA099).
    // Sans le droit : cases désactivées → l'utilisateur cède l'intégralité, sans
    // pouvoir en retirer.
    const peutDecocher = rightsService("HLA099");

    const rowSelection = {
        selectedRowKeys: selectedRows.map(r => r.key),
        onChange: (selectedRowKeys, selectedRows) => {
            onSelectionChange(selectedRows);
        },
        getCheckboxProps: () => ({
            disabled: isValidated || isDisabled || !peutDecocher,
        }),
    };



    const handlePrint = () => {
        setIsPrinting(true);
        // Petit délai pour s'assurer que le DOM est mis à jour
        setTimeout(() => {
            onPrint();
            // Remettre l'état après l'impression
            setTimeout(() => setIsPrinting(false), 100);
        }, 100);
    };

    const fd = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

    // Totaux PAR COLONNE, en valeur absolue (cohérent avec l'affichage des colonnes).
    // Total sur les lignes sélectionnées ; si AUCUNE sélection, sur TOUTES les lignes —
    // cohérent avec la validation (« rien de sélectionné » = tout) et indispensable à la
    // modale « Voir les bordereaux » en lecture seule, qui passe selectedRows = [].
    const estSelectionne = (it) => !selectedRows?.length || selectedRows.find((r) => r.key === it.key);
    const sommeSelection = (champ) => (data || []).reduce(
        (sum, it) => sum + (estSelectionne(it) ? Math.abs(it[champ] || 0) : 0), 0);
    const totaux = {
        pnPool: sommeSelection('primeNettePool'),
        pnComp: sommeSelection('primeNetteCompagnie'),
        acc: sommeSelection('accessoires'),
        taxes: sommeSelection('taxes'),
        ttc: sommeSelection('primeACeder'),
    };

    const handleExportExcel = () => {
        exportBordereauExcel({
            titre: `BORDEREAU DES ANNULATIONS DU MOIS DE ${moisPeriodeLabel(periode).toUpperCase()}`,
            metaLines: [`Catégorie ${categorie?.codeCategorie || ''}`, `Compagnie : ${compagnie?.raisonSociale || ''}`],
            headers: ['N° Police', 'Avenant', 'Assuré', 'Effet', 'Échéance', 'Catégorie',
                'PN Pool', 'PN Compagnie', 'Accessoires', 'Taxes', 'Prime TTC'],
            rows: (data || []).map((it) => [
                it.numeroPolice, it.avenant, it.assure, fd(it.effet), fd(it.echeance), it.categorie,
                Math.trunc(Math.abs(it.primeNettePool || 0)), Math.trunc(Math.abs(it.primeNetteCompagnie || 0)),
                Math.trunc(Math.abs(it.accessoires || 0)), Math.trunc(Math.abs(it.taxes || 0)),
                Math.trunc(Math.abs(it.primeACeder || 0)),
            ]),
            totalRow: ['TOTAL', '', '', '', '', '',
                Math.trunc(totaux.pnPool), Math.trunc(totaux.pnComp), Math.trunc(totaux.acc),
                Math.trunc(totaux.taxes), Math.trunc(totaux.ttc)],
            nomFichier: `Bordereau_Annulations_${categorie?.codeCategorie || ''}_${moisPeriodeLabel(periode)}`,
        });
    };

    return (
        <div>
            {/* Version affichage */}
            <div className="mb-4" style={{ padding: 15 }}>
                <Table
                    rowKey="codeId"
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={data}
                    pagination={{ pageSize: 10 }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                {/* colSpan = 7 (et non 6) : couvre la colonne de SÉLECTION (case à
                                    cocher de rowSelection) + les 6 colonnes descriptives, sinon la
                                    ligne de totaux est décalée d'une colonne vers la gauche. */}
                                <Table.Summary.Cell index={0} colSpan={7}>
                                    <strong>TOTAL</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right"><strong>{displayNumbers(totaux.pnPool)}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right"><strong>{displayNumbers(totaux.pnComp)}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right"><strong>{displayNumbers(totaux.acc)}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right"><strong>{displayNumbers(totaux.taxes)}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align="right"><strong>{displayNumbers(totaux.ttc)} F CFA</strong></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                <div className="mt-3 d-flex gap-2">
                    <button
                        type="button"
                        onClick={onValidate}
                        disabled={isDisabled || isValidated || loading}
                        className={isValidated ? "btn btn-success" : "btn btn-warning"}
                    >
                        {loading && (
                            <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                        )}
                        <i className="tf-icons bx bxs-badge-check bx-18px me-2"></i>
                        <span className="align-middle">
                            {loading ? 'Validation...' : isValidated
                                ? `Annulations ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode?.split("-")?.[0] : ''} validées`
                                : `Valider les annulations ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode?.split("-")?.[0] : ''}`
                            }
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={handlePrint}
                        className="btn btn-label-pinterest"
                    >
                        <i className="tf-icons bx bxs-file-pdf bx-18px me-2"></i>
                        Imprimer (PDF)
                    </button>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="btn btn-label-success"
                    >
                        <i className="tf-icons bx bxs-file-export bx-18px me-2"></i>
                        Exporter Excel
                    </button>
                </div>
            </div>

            {/* Version impression (cachée) */}
            <div ref={ref} style={{
                position: isPrinting ? 'relative' : 'absolute',
                left: isPrinting ? 'auto' : '-9999px',
                visibility: isPrinting ? 'visible' : 'hidden'
            }}>
                <style>
                    {`
                        @media print {
                            @page {
                                size: landscape;
                                margin: 20mm;
                            }
                            body {
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                        }
                    `}
                </style>
                <div style={{ padding: '10mm 8mm', fontFamily: 'Arial, sans-serif' }}>
                    <BordereauPrintTable
                        titre="BORDEREAU DES ANNULATIONS"
                        categorie={categorie}
                        compagnie={compagnie}
                        periode={periode}
                        logoPreviewUrl={logoPreviewUrl}
                        data={data}
                        columns={[
                            { title: 'N° Police', get: (it) => it.numeroPolice },
                            { title: 'Avenant', get: (it) => it.avenant },
                            { title: 'Assuré', get: (it) => it.assure },
                            { title: 'Effet', get: (it) => (it.effet ? new Date(it.effet).toLocaleDateString('fr-FR') : '') },
                            { title: 'Échéance', get: (it) => (it.echeance ? new Date(it.echeance).toLocaleDateString('fr-FR') : '') },
                            { title: 'Catégorie', get: (it) => it.categorie },
                            { title: 'PN Pool', align: 'right', get: (it) => displayNumbers(Math.abs(it.primeNettePool || 0)) },
                            { title: 'PN Compagnie', align: 'right', get: (it) => displayNumbers(Math.abs(it.primeNetteCompagnie || 0)) },
                            { title: 'Accessoires', align: 'right', get: (it) => displayNumbers(Math.abs(it.accessoires || 0)) },
                            { title: 'Taxes', align: 'right', get: (it) => displayNumbers(Math.abs(it.taxes || 0)) },
                            { title: 'Prime TTC', align: 'right', get: (it) => displayNumbers(Math.abs(it.primeACeder || 0)) },
                        ]}
                        totalRow={['', '', '', '', '', '',
                            displayNumbers(totaux.pnPool), displayNumbers(totaux.pnComp),
                            displayNumbers(totaux.acc), displayNumbers(totaux.taxes),
                            `${displayNumbers(totaux.ttc)} F CFA`]}
                    />
                </div>
            </div>
        </div>
    );
});

BordereauAnnulations.displayName = 'BordereauAnnulations';

export default BordereauAnnulations;